import { useCallback } from 'react';

import { arrayEntries } from '@aline/common';
import { link } from '@aline/routes';
import type { ApolloLink, Reference } from '@apollo/client';
import { gql, makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { uniqBy } from 'ramda';
import { useNavigate } from 'react-router-dom';
import { getFileFormat } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { documentTypename, useDispatchSuccessSnack } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks/document';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { v4 as uuid4 } from 'uuid';

export function useGetFileVersion(fileVersionId?: UUID) {
  const { data, loading } = Apollo.useGetFileVersionQuery({
    skip: !fileVersionId,
    variables: { fileVersionId: fileVersionId ?? '' },
  });

  return {
    fileVersion: data?.fileVersion ?? null,
    loading,
  };
}

export function useGoToLatestVersion() {
  const navigate = useNavigate();
  const { document } = useGetCurrentDoc();
  const goToLatestVersion = useCallback(() => {
    const versionHistoryHref = getDocumentLatestVersionHref(document);
    if (versionHistoryHref) navigate(versionHistoryHref);
  }, [document, navigate]);

  return { goToLatestVersion };
}

export function getDocumentLatestVersionHref(
  document: { id: string; latestVersion: { id: string } } | null | undefined
) {
  if (!document?.latestVersion.id) return undefined;
  return link.DocumentDetailVersionDetail({
    documentId: document.id,
    versionId: document.latestVersion.id,
  });
}

export function useCreateStaticFileVersion(): {
  createStaticFileVersion: (
    documentId: UUID,
    options: Parameters<Apollo.CreateStaticFileVersionMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateStaticFileVersionMutation>>;
} {
  const [createStaticFileVersion] = Apollo.useCreateStaticFileVersionMutation();
  const safeCreateStaticFileVersion = useCallback(
    (
      documentId: UUID,
      options: Parameters<Apollo.CreateStaticFileVersionMutationHookResult[0]>[0]
    ) => {
      return createStaticFileVersion({
        ...options,
        update(cache, { data }) {
          const newVersionRef = cache.writeFragment({
            data: data?.createStaticFileVersion.fileVersion,
            fragment: gql`
              fragment NewVersion on FileVersionInterface {
                __typename
                id
              }
            `,
          });
          cache.modify({
            fields: {
              latestVersion: ref => newVersionRef ?? ref,
              orderedVersions: (existingVersionRefs: readonly Reference[] = [], { readField }) => {
                return uniqBy(
                  ref => readField<UUID>('id', ref),
                  newVersionRef ? [newVersionRef, ...existingVersionRefs] : existingVersionRefs
                );
              },
            },
            id: cache.identify({ __typename: documentTypename, id: documentId }),
          });
        },
      });
    },
    [createStaticFileVersion]
  );

  return {
    createStaticFileVersion: safeCreateStaticFileVersion,
  };
}

export function useApplySignedVersionOutsideAline(): {
  applySignedVersionOutsideAline: (
    documentId: UUID,
    options: Parameters<Apollo.ApplySignedVersionOutsideAlineMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.ApplySignedVersionOutsideAlineMutation>>;
  loading: boolean;
} {
  const [applySignedVersionOutsideAline, { loading }] =
    Apollo.useApplySignedVersionOutsideAlineMutation();
  const safeApply = useCallback(
    (
      documentId: UUID,
      options: Parameters<Apollo.ApplySignedVersionOutsideAlineMutationHookResult[0]>[0]
    ) => {
      return applySignedVersionOutsideAline({
        ...options,
        update(cache, { data }, { variables }) {
          const newVersionRef = cache.writeFragment({
            data: data?.applySignedVersionOutsideAline.fileVersion,
            fragment: gql`
              fragment NewSignedVersion on FileVersionInterface {
                __typename
                id
              }
            `,
          });
          cache.modify({
            fields: {
              isSigned: () => true,
              latestVersion: ref => newVersionRef ?? ref,
              orderedVersions: (existingVersionRefs: readonly Reference[] = [], { readField }) => {
                return uniqBy(
                  ref => readField<UUID>('id', ref),
                  newVersionRef ? [newVersionRef, ...existingVersionRefs] : existingVersionRefs
                );
              },
            },
            id: cache.identify({ __typename: documentTypename, id: documentId }),
          });

          const sourceDocumentId = variables?.input.sourceDocumentId;
          if (!sourceDocumentId) return;

          cache.evict({
            id: cache.identify({ __typename: documentTypename, id: sourceDocumentId }),
          });
          cache.gc();
        },
      });
    },
    [applySignedVersionOutsideAline]
  );

  return { applySignedVersionOutsideAline: safeApply, loading };
}

export function useCreateStaticFileVersions(): {
  createStaticFileVersions: (
    options: Parameters<Apollo.CreateStaticFileVersionsMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateStaticFileVersionsMutation>>;
} {
  const [createStaticFileVersions] = Apollo.useCreateStaticFileVersionsMutation();
  const safeCreateStaticFileVersions = useCallback(
    (options: Parameters<Apollo.CreateStaticFileVersionsMutationHookResult[0]>[0]) => {
      return createStaticFileVersions({
        ...options,
        update(cache, { data }) {
          const records = options.variables?.input.records ?? [];
          const createdVersions = data?.createStaticFileVersions ?? [];

          for (const [index, record] of arrayEntries(records)) {
            const createdVersion = Object.hasOwn(createdVersions, index)
              ? createdVersions[index]
              : null;
            if (!createdVersion) continue;

            const newVersionRef = cache.writeFragment({
              data: createdVersion,
              fragment: gql`
                fragment NewVersion on FileVersionInterface {
                  __typename
                  id
                }
              `,
            });

            cache.modify({
              fields: {
                latestVersion: ref => newVersionRef ?? ref,
                orderedVersions: (
                  existingVersionRefs: readonly Reference[] = [],
                  { readField }
                ) => {
                  return uniqBy(
                    ref => readField<UUID>('id', ref),
                    newVersionRef ? [newVersionRef, ...existingVersionRefs] : existingVersionRefs
                  );
                },
              },
              id: cache.identify({ __typename: documentTypename, id: record.documentId }),
            });
          }
        },
      });
    },
    [createStaticFileVersions]
  );

  return {
    createStaticFileVersions: safeCreateStaticFileVersions,
  };
}

export function useDeleteFileVersion(): {
  deleteFileVersion: (args: {
    documentId: UUID;
    isDeletingLatestVersion: boolean;
    options: Parameters<Apollo.DeleteFileVersionMutationHookResult[0]>[0];
  }) => Promise<ApolloLink.Result<Apollo.DeleteFileVersionMutation>>;
  loading: boolean;
} {
  const [deleteFileVersion, { loading }] = Apollo.useDeleteFileVersionMutation();

  return {
    deleteFileVersion: ({ documentId, isDeletingLatestVersion, options }) => {
      return deleteFileVersion({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;

          cache.modify({
            fields: {
              isSigned: isSigned => (!isDeletingLatestVersion ? isSigned : false),
              latestVersion: (versionRef, { DELETE }) => {
                // We're not deleting the latestVersion so it's a no-op.
                if (!isDeletingLatestVersion) return versionRef;

                // Invalidate Document.latestVersion causing Apollo to refetch the field from the server.
                return DELETE;
              },
              orderedVersions: (existingVersionRefs: readonly Reference[] = [], { readField }) => {
                return existingVersionRefs.filter(
                  (ref: Reference) => readField('id', ref) !== options?.variables?.id
                );
              },
            },
            id: cache.identify({ __typename: documentTypename, id: documentId }),
          });
        },
      });
    },
    loading,
  };
}

interface StaticFileWithLocation extends Pick<Apollo.StaticFileVersion, '__typename' | 'id'> {
  fileLocation: Pick<Apollo.DocFile, 'mimetype'>;
}

interface DocumentCheckableForConversion extends Pick<
  Apollo.Document,
  | 'id'
  | 'isSigned'
  | '__typename'
  | 'conversionStartedAt'
  | 'conversionFinishedAt'
  | 'conversionFailedAt'
> {
  latestVersion: Pick<Apollo.DocumentVersion, '__typename' | 'id'> | StaticFileWithLocation;
  workflow: Pick<Apollo.Workflow, 'state' | '__typename'> | null;
}

export const convertingToAlineVar = makeVar<boolean>(false);

export function useConvertingToAline() {
  const convertingToAline = useReactiveVar(convertingToAlineVar);
  return { convertingToAline };
}

export function useConvertToAline() {
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Document converted to Aline.',
  });
  const { convertingToAline } = useConvertingToAline();
  const [convertToAline] = Apollo.useImportDocumentMutation();
  const safeConvertToAline = useCallback(
    async (documentId: UUID) => {
      convertingToAlineVar(true);
      const { data } = await convertToAline({
        variables: { documentId: documentId },
      });
      if (data) dispatchSuccessSnack();
      convertingToAlineVar(false);
    },
    [convertToAline, dispatchSuccessSnack]
  );
  const safeGetIsDocumentConvertable = useCallback(
    (document: DocumentCheckableForConversion | null) => {
      const wasSentForSignature = document?.workflow
        ? getWorkflowWasSentForSig(document.workflow)
        : false;

      return (
        document?.latestVersion.__typename === 'StaticFileVersion' &&
        getFileFormat(document.latestVersion.fileLocation.mimetype) === 'msWord' &&
        !wasSentForSignature &&
        !document.isSigned
      );
    },
    []
  );
  const safeGetIsActivelyConverting = useCallback((doc: DocumentCheckableForConversion | null) => {
    return Boolean(
      doc?.conversionStartedAt && !doc.conversionFinishedAt && !doc?.conversionFailedAt
    );
  }, []);

  return {
    convertToAline: safeConvertToAline,
    getIsActivelyConverting: safeGetIsActivelyConverting,
    getIsDocumentConvertable: safeGetIsDocumentConvertable,
    loading: convertingToAline,
  };
}
